<template>
  <div class="container">
    <div
      class="filters"
      v-if="!layoutLocked"
      :style="{ position: filterPosition }"
    >
      <font-awesome-icon
        style="padding: 5px; color: #de6264; cursor: pointer;z-index: 2;"
        :icon="['fas', 'trash-alt']"
        @click="$parent.$emit('delete')"
        class="white-bg"
        
      />
      <font-awesome-icon
        style="padding: 5px; border-top-right-radius: inherit; cursor: pointer;z-index: 2;"
        :icon="['fas', 'cog']"
        @click="openFilters = !openFilters"
        class="white-bg"
      />
      <filter-menu v-if="openFilters" @closeFilters="closeFilters">
        <campaign-select
          v-if="configContainsProperty('campaignNames')"
          :items="conf.campaignNames"
          :argType="argType"
          :type="campaignTypes"
          @selected="$emit('campaign-selected', $event)"
          @removed="$emit('campaign-removed', $event)"
          ref="campaign-select"
        />

        <state-select
          v-if="configContainsProperty('agentStates')"
          :items="conf.agentStates"
          @selected="$emit('state-selected', $event)"
          @removed="$emit('state-removed', $event)"
          ref="state-select"
        />

        <queue-select
          v-if="configContainsProperty('queueNumbers')"
          :items="conf.queueNumbers"
          @selected="$emit('queue-selected', $event)"
          @removed="$emit('queue-removed', $event)"
          ref="queue-select"
        />

        <property-select
          v-if="configContainsProperty('agentProperties')"
          :items="conf.agentProperties"
          propertyType="agent"
          argType="agentArgs"
          @selected="$emit('property-selected', $event)"
          @removed="$emit('property-removed', $event)"
          ref="property-select"
        />

        <property-select
          v-if="configContainsProperty('campaignProperties')"
          :items="conf.campaignProperties"
          propertyType="campaign"
          argType="campaignArgs"
          @selected="$emit('property-selected', $event)"
          @removed="$emit('property-removed', $event)"
          ref="property-select"
        />

        <property-select
          v-if="configContainsProperty('queueProperties')"
          :items="conf.queueProperties"
          propertyType="queue"
          argType="queueArgs"
          @selected="$emit('property-selected', $event)"
          @removed="$emit('property-removed', $event)"
          ref="property-select"
        />

        <slot name="filters"> </slot>
      </filter-menu>
    </div>
    <slot name="control"></slot>
  </div>
</template>

<script>
import { CampaignSelect, PropertySelect, StateSelect } from "@/components";

export default {
  name: "ComponentBase",

  components: {
    CampaignSelect,
    PropertySelect,
    StateSelect,
  },

  props: {
    // * Name of the component that's using this base
    parentName: {
      required: true,
      value: String,
    },

    // * Toolbox config
    config: {
      required: true,
      value: Object,
    },

    // * Affects which diamond args object gets updated by this object
    dataType: {
      required: true,
      value: String,
      validator: function (value) {
        return ["agent", "campaign", "queue"].indexOf(value) !== -1;
      },
    },

    // * Changes what campaign types will show in the CampaignName list if it's being used
    campaignTypes: {
      required: false,
      value: String,
      validator: function (value) {
        return ["in", "out", "all"].indexOf(value) !== -1;
      },
    },

    // * Accepts relative or absolute
    filterPosition: {
      required: false,
      value: String,
      default: () => "absolute",
    },
  },

  data() {
    return {
      openFilters: false,
      argType: "",
    };
  },

  mounted() {
    this.checkMissingKeys(); //If a new property has been added to the toolbox for this component, i's default value will be added to this component
    this.argType = this.dataType + "Args"; //Args objects are called for example "campaignArgs", we need to append "Args" to the data type

    // * Add to args objects based on passed props
    this.createInitialArgs();
    this.$emit("loaded", true);
  },

  computed: {
    conf() {
      return this.config;
    },

    layoutLocked() {
      return this.$store.getters.locked;
    },
  },

  methods: {
    // ============================
    // * Renderer mutators
    // ============================

    closeFilters() {
      this.openFilters = false;
    },

    // ============================
    // * Core component methods
    // ============================

    checkMissingKeys() {
      var toolboxConf =
        this.$toolbox[
          this.$toolbox.findIndex(
            (x) => this.removeSpaces(x.name) == this.parentName
          )
        ].config;
      var keys = this.getMissingKeys(toolboxConf, this.conf);
      keys.forEach((x) => {
        this.conf[x] = JSON.parse(JSON.stringify(toolboxConf[x]));
      });
    },

    configContainsProperty(prop) {
      return Object.prototype.hasOwnProperty.call(this.conf, prop);
    },

    createInitialArgs() {
      if (this.configContainsProperty("campaignNames")) {
        this.conf.campaignNames.forEach((x) => {
          var index = this.$store.getters[
            this.argType
          ]._CampaignNames.findIndex((x) => x.campaignName == x);
          if (index == -1)
            this.$store.getters[this.argType]._CampaignNames.push({
              campaignName: x,
              count: 1,
            });
          else
            this.$store.getters[this.argType]._CampaignNames[index].count += 1;
        });
      }

      if (this.configContainsProperty("agentStates")) {
        this.conf.agentStates.forEach((x) => {
          var index = this.$store.getters.agentArgs._AgentStates.findIndex(
            (x) => x.agentState == x
          );
          if (index == -1)
            this.$store.getters.agentArgs._AgentStates.push({
              agentState: x,
              count: 1,
            });
          else this.$store.getters.agentArgs._AgentStates[index].count += 1;
        });
      }

      if (this.configContainsProperty("campaignProperties")) {
        this.conf.campaignProperties.forEach((x) => {
          var index = this.$store.getters.campaignArgs._Properties.findIndex(
            (x) => x.property == x
          );
          if (index == -1)
            this.$store.getters.campaignArgs._Properties.push({
              property: x,
              count: 1,
            });
          else this.$store.getters.campaignArgs._Properties[index].count += 1;
        });
      }

      if (this.configContainsProperty("agentProperties")) {
        this.conf.agentProperties.forEach((x) => {
          var index = this.$store.getters.agentArgs._Properties.findIndex(
            (x) => x.property == x
          );
          if (index == -1)
            this.$store.getters.agentArgs._Properties.push({
              property: x,
              count: 1,
            });
          else this.$store.getters.agentArgs._Properties[index].count += 1;
        });
      }

      if (this.configContainsProperty("queueProperties")) {
        this.conf.queueProperties.forEach((x) => {
          var index = this.$store.getters.queueArgs._Properties.findIndex(
            (x) => x.property == x
          );
          if (index == -1)
            this.$store.getters.queueArgs._Properties.push({
              property: x,
              count: 1,
            });
          else this.$store.getters.queueArgs._Properties[index].count += 1;
        });
      }

      if (this.configContainsProperty("queueNumbers")) {
        this.conf.queueNumbers.forEach((x) => {
          var index = this.$store.getters.queueArgs._QueueNumbers.findIndex(
            (x) => x.queueNum == x
          );
          if (index == -1)
            this.$store.getters.queueArgs._QueueNumbers.push({
              queueNum: x,
              count: 1,
            });
          else this.$store.getters.queueArgs._QueueNumbers[index].count += 1;
        });
      }

      this.$diamond.sendAgentParameters(this.$store.getters.agentArgs);
      this.$diamond.sendCampaignParameters(this.$store.getters.campaignArgs);
      this.$diamond.sendQueueParameters(this.$store.getters.queueArgs);
    },
  },
};
</script>

<style scoped>
.container {
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

.white-bg {
  background-color: rgba(255, 255, 255, 0.95);
}

.filters {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding: 0;
  margin-right: -40px;
  position: relative;
  border-radius: inherit;
}
</style>